import { CumulativeBooster } from "../cumulative_booster.js";
import { CumulativeBoosterID } from "../cumulative_booster_id.js";

export interface FireworksTarget
{
  x: number;
  y: number;
  z: number;
}

export interface FireworksBoosterContext
{
  setFireworksActive( active: boolean ): void;
  getRandomFireworksTargets(
    count: number,
    excluded_targets?: readonly FireworksTarget[]
  ): FireworksTarget[];
  fireFireworkAt( target: FireworksTarget, passive_rocket?: boolean ): void;
}

export class FireworksBooster extends CumulativeBooster<FireworksBoosterContext>
{
  public static readonly DEFAULT_FIREWORK_COUNT = 3;
  public static readonly DEFAULT_SHOT_INTERVAL_SECONDS = 0.15;

  public readonly fireworkCount: number;
  public readonly shotInterval: number;

  private targets: FireworksTarget[] = [];
  private next_target_index = 0;
  private time_to_next_firework = 0;
  private primary_firework_fired = false;

  public constructor(
    firework_count = FireworksBooster.DEFAULT_FIREWORK_COUNT,
    shot_interval = FireworksBooster.DEFAULT_SHOT_INTERVAL_SECONDS
  )
  {
    super( CumulativeBoosterID.Fireworks );

    if ( !Number.isFinite( firework_count ) || firework_count <= 0 )
      throw new Error( "Fireworks count must be a finite positive number" );

    if ( !Number.isFinite( shot_interval ) || shot_interval < 0 )
      throw new Error( "Fireworks shot interval must be a finite non-negative number" );

    this.fireworkCount = Math.floor( firework_count );
    this.shotInterval = shot_interval;
  }

  /** Called after the firework aimed by the player has actually been fired. */
  public onPrimaryFireworkFired( primary_target: FireworksTarget ): void
  {
    if ( !this.isActive || this.primary_firework_fired )
      return;

    const context = this.activeContext;
    if ( context === null )
      return;

    // Only the first aimed shot belongs to the booster. The remaining
    // fireworks are emitted below by the booster itself, so disarm manual
    // fire immediately; otherwise rapid taps can launch extra rockets while
    // the scheduled fireworks are still being released.
    context.setFireworksActive( false );
    this.primary_firework_fired = true;
    this.targets = context.getRandomFireworksTargets(
      this.fireworkCount - 1,
      [ primary_target ]
    );
    this.time_to_next_firework = this.shotInterval;

    if ( this.targets.length === 0 )
      this.complete();
  }

  protected override onActivate( context: FireworksBoosterContext ): void
  {
    context.setFireworksActive( true );
  }

  protected override onUpdate( context: FireworksBoosterContext, dt: number ): void
  {
    if ( !this.primary_firework_fired )
      return;

    this.time_to_next_firework -= dt;

    while ( this.next_target_index < this.targets.length && this.time_to_next_firework <= 0 )
    {
      context.fireFireworkAt( this.targets[this.next_target_index] );
      this.next_target_index++;
      this.time_to_next_firework += this.shotInterval;
    }

    if ( this.next_target_index >= this.targets.length )
      this.complete();
  }

  protected override onDeactivate( context: FireworksBoosterContext, _cancelled: boolean ): void
  {
    context.setFireworksActive( false );
  }

  protected override onReset(): void
  {
    this.targets = [];
    this.next_target_index = 0;
    this.time_to_next_firework = 0;
    this.primary_firework_fired = false;
  }
}
